import { ReactComponent as AlertError } from "assets/svg/alert-error.svg";
import { ReactComponent as AlertInfo } from "assets/svg/alert-info.svg";
import { ReactComponent as AlertWarning } from "assets/svg/alert-question.svg";
import { ReactComponent as AlertSuccess } from "assets/svg/alert-success.svg";
import classNames from "classnames";
import { AnimatePresence } from "framer-motion";
import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { useMount } from "react-use";

import { store } from "store";

export enum NotificationTypes {
  "success" = "success",
  "error" = "error",
  "warning" = "warning",
  "info" = "info",
  "form" = "form",
}

export interface NotificationProps {
  type: "success" | "error" | "warning" | "info" | "form";
  showsCloseAction?: boolean;
  autoDismissible?: number;
  scrollTo?: boolean;
  cta?: React.ReactNode;
  identifier?: string;
  floating?: boolean;
}

const isInViewport = function (bounding: DOMRect) {
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= windowHeight &&
    bounding.right <= windowWidth
  );
};

const NotificationInner: React.FC<NotificationProps & { close(): void }> = ({
  children,
  type,
  showsCloseAction,
  close,
  scrollTo,
  cta,
  floating,
}) => {
  const element = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (element.current && scrollTo) {
      const position = element.current.getBoundingClientRect();

      if (!isInViewport(position)) {
        window.scrollBy({
          behavior: "smooth",
          top: position.top - 32,
        });
      }
    }
  }, [scrollTo]);

  const AlertIcon = function () {
    let Icon = undefined;
    switch (type) {
      case "success":
        Icon = AlertSuccess;
        break;
      case "error":
        Icon = AlertError;
        break;
      case "warning":
        Icon = AlertWarning;
        break;
      case "info":
        Icon = AlertInfo;
        break;

      default:
        return null;
    }

    return <Icon className="o-svg-icon">{type}</Icon>;
  };

  return (
    <div
      ref={element}
      className={classNames([
        "c-alert",
        "c-alert--small@xs",
        "c-alert--row@md",
        "c-color--invert",
        `c-alert--${type}`,
        { "c-alert--float@xs": floating ?? false },
        { "c-alert--anim": showsCloseAction ?? false },
      ])}
    >
      <AlertIcon />
      <div className="c-alert__col c-alert__body">{children}</div>
      {typeof cta !== "undefined" && <div className="c-alert__col c-alert__footer">{cta}</div>}
      {showsCloseAction && (
        <div
          className="c-alert__close"
          onClick={(event) => {
            event.preventDefault();
            element.current?.classList.remove("c-alert--anim");
            element.current?.classList.add("c-alert--anim-hidden");
            close();
          }}
        >
          close
        </div>
      )}
    </div>
  );
};

export const Notification: React.FC<NotificationProps> = (props) => {
  const { autoDismissible } = props;
  const [isActive, setIsActive] = useState(false);

  useMount(() => {
    let timeout: NodeJS.Timeout | undefined;
    setIsActive(true);
    if (autoDismissible) {
      timeout = setTimeout(() => {
        hideNotification();
      }, autoDismissible);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  });

  function hideNotification() {
    setIsActive(false);
    if (props.identifier) {
      store.notifications.dismissNotification(props.identifier);
    }
  }

  return (
    <AnimatePresence>
      {isActive && <NotificationInner {...props} close={hideNotification} />}
    </AnimatePresence>
  );
};

export default Notification;
